import { api } from "@/lib/apiClient";

export type IncomeStatementLine = {
  account_id: number;
  account_code: string;
  account_name: string;
  account_class: string;
  account_group: string | null;
  amount: number;
};

export type IncomeStatementSection = {
  key: string;
  label: string;
  lines: IncomeStatementLine[];
  total: number;
};

export type IncomeStatement = {
  filters: {
    date_from: string | null;
    date_to: string | null;
    hide_zero: boolean;
  };
  sections: IncomeStatementSection[];
  totals: {
    income: number;
    cos: number;
    gross_profit: number;
    expense: number;
    net_income: number;
  };
};

type ApiIncomeStatement = {
  filters?: {
    date_from?: string | null;
    date_to?: string | null;
    hide_zero?: boolean;
  };
  sections?: Array<Record<string, unknown>>;
  totals?: {
    income?: number;
    cos?: number;
    gross_profit?: number;
    expense?: number;
    net_income?: number;
  };
};

export type FetchIncomeStatementParams = {
  dateFrom?: string | null;
  dateTo?: string | null;
  hideZero?: boolean;
};

function queryParams(params: FetchIncomeStatementParams = {}) {
  return {
    ...(params.dateFrom ? { date_from: params.dateFrom } : {}),
    ...(params.dateTo ? { date_to: params.dateTo } : {}),
    ...(params.hideZero ? { hide_zero: 1 } : {}),
  };
}

async function downloadBlob(url: string, params: FetchIncomeStatementParams, filename: string): Promise<void> {
  const response = await api.get(url, { params: queryParams(params), responseType: "blob" });
  const blob = new Blob([response.data], {
    type: response.headers["content-type"] || "application/octet-stream",
  });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
}

function mapLine(row: Record<string, unknown>): IncomeStatementLine {
  return {
    account_id: Number(row.account_id ?? 0),
    account_code: String(row.account_code ?? ""),
    account_name: String(row.account_name ?? ""),
    account_class: String(row.account_class ?? ""),
    account_group: (row.account_group as string | null) ?? null,
    amount: Number(row.amount ?? 0),
  };
}

function mapSection(row: Record<string, unknown>): IncomeStatementSection {
  const lines = Array.isArray(row.lines) ? row.lines.map((line) => mapLine(line as Record<string, unknown>)) : [];
  return {
    key: String(row.key ?? ""),
    label: String(row.label ?? ""),
    lines,
    total: Number(row.total ?? 0),
  };
}

export async function fetchIncomeStatement(params: FetchIncomeStatementParams = {}): Promise<IncomeStatement> {
  const response = await api.get<{ data: ApiIncomeStatement }>("/api/v1/accounts/income-statement", {
    params: queryParams(params),
  });
  const data = response.data?.data ?? {};
  return {
    filters: {
      date_from: data.filters?.date_from ?? null,
      date_to: data.filters?.date_to ?? null,
      hide_zero: Boolean(data.filters?.hide_zero),
    },
    sections: Array.isArray(data.sections) ? data.sections.map(mapSection) : [],
    totals: {
      income: Number(data.totals?.income ?? 0),
      cos: Number(data.totals?.cos ?? 0),
      gross_profit: Number(data.totals?.gross_profit ?? 0),
      expense: Number(data.totals?.expense ?? 0),
      net_income: Number(data.totals?.net_income ?? 0),
    },
  };
}

export async function downloadIncomeStatementPdf(params: FetchIncomeStatementParams = {}): Promise<void> {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  await downloadBlob("/api/v1/accounts/income-statement/pdf", params, `income-statement-${stamp}.pdf`);
}

export async function downloadIncomeStatementExcel(params: FetchIncomeStatementParams = {}): Promise<void> {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  await downloadBlob("/api/v1/accounts/income-statement/excel", params, `income-statement-${stamp}.csv`);
}
